import { Box, Typography, useTheme, Tooltip } from "@mui/material";
import { tokens } from "../../theme";
import LazyCardImage from "./LazyCardImage";
import { memo, useState, useEffect } from "react";
import splinterlandsSettingsService from "../../services/SplinterlandsSettingsService";
import { 
  CARD_EDITIONS,
  getElementInfoByColor,
  getRarityInfoByValue,
  getCardTypeInfoByValue
} from "../../constants/filterConstants";

/**
 * CardItem Komponente für die Darstellung von Splinterlands Karten im Market
 * 
 * @param {Object} props - Die Properties der Komponente
 * @param {Object} props.card - Die Kartenobjektdaten
 * @param {Object} props.details - Die Details der Karte
 * @param {Boolean} props.showBaseImages - Ob Basis- oder Level-4-Bilder angezeigt werden
 * @param {String} props.price - Der formatierte Preis der Karte
 * @param {Object} props.priceTracking - Preistracking-Daten von unserer API
 * @param {Number} props.refreshImageKey - Key zum Erzwingen einer Aktualisierung
 * @param {Function} props.onCardClick - Callback für Klick-Events auf der Karte
 * @returns {JSX.Element} - Die gerenderte Karten-Komponente
 * 
 */
const CardItem = memo(({ card, details, showBaseImages, price, priceTracking, refreshImageKey, onCardClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isHovered, setIsHovered] = useState(false);
  const [, setForceUpdate] = useState(false); // for settings update - only setter used
  
  // Effect to subscribe to settings updates for edition names
  useEffect(() => {
    const unsubSettings = splinterlandsSettingsService.subscribe(() => setForceUpdate(f => !f));
    
    return () => {
      unsubSettings();
    };
  }, []);

  // Kartentyp für Hintergrundfarbe und Badge bestimmen
  const getTypeColor = () => {
    switch (details?.type) {
      case "Monster": return colors.brown[600];
      case "Summoner": return colors.blueAccent[600];
      case "Arcana": return colors.yellow[700];
      default: return colors.primary[500];
    }
  };

  // Rand und Highlight-Farbe für Foil-Karten
  const getBorderColor = () => {
    if (card.foil === 1) return "#FFD700"; // Gold Foil
    if (card.foil === 2) return "#FFA500"; // Gold Arcana
    if (card.foil === 3) return "#333333"; // Black Foil
    if (card.foil === 4) return "#4B0082"; // Black Arcana
    return colors.primary[400]; // Regular
  };
  
  // Element der Karte bestimmen
  const getElement = () => {
    if (!details?.color) return null;
    return getElementInfoByColor(details.color);
  };
  
  // Rarität Info der Karte bestimmen
  const getRarityInfo = () => {
    return getRarityInfoByValue(details?.rarity);
  };
  
  // Kartentyp Info bestimmen
  const getCardTypeInfo = () => {
    return getCardTypeInfoByValue(details?.type);
  };

  // Helper to get edition info (name, icon, color) from CARD_EDITIONS constant
  const getEditionInfo = (editionId) => {
    if (editionId === undefined || editionId === null) {
      return { name: 'Unknown', icon: null, color: '#888888' };
    }
    
    const edition = CARD_EDITIONS.find(ed => ed.id === editionId);
    return edition || { name: 'Unknown', icon: null, color: '#888888' };
  };

  const cardTypeColor = getTypeColor();
  const borderColor = getBorderColor();
  const element = getElement();
  const editionInfo = getEditionInfo(details?.edition);
  const rarityInfo = getRarityInfo();
  const cardTypeInfo = getCardTypeInfo();

  // Preis-Logik - API liefert bereits USD-Preise
  let totalUsdPrice = 0;
  let usdPriceBcx = 0;

  // Preis pro BCX - direkt aus API in USD
  if (card.low_price_bcx !== undefined && card.low_price_bcx !== null) {
    usdPriceBcx = Number(card.low_price_bcx);
  }

  // Gesamtpreis der Karte - direkt aus API in USD
  if (card.low_price !== undefined && card.low_price !== null) {
    totalUsdPrice = Number(card.low_price);
  }

  return (
    <Box 
      onMouseEnter={() => setIsHovered(true)} 
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onCardClick && onCardClick(card, details)}
      sx={{
        backgroundColor: colors.primary[400], // Set card background to primary[400]
        transition: "transform 0.2s, box-shadow 0.3s",
        cursor: 'pointer',
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: isHovered ? `0 8px 16px rgba(0,0,0,0.3)` : "0 4px 6px rgba(0,0,0,0.1)",
        transform: isHovered ? "translateY(-8px)" : "none",
        position: "relative",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Card Type Indicator (Foil Types) */}
      {card.foil > 0 && (
        <Box 
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            backgroundColor: borderColor,
            color: card.foil === 3 || card.foil === 4 ? "#FFF" : "#000",
            padding: "2px 8px",
            borderBottomLeftRadius: "8px",
            fontSize: "0.8rem",
            fontWeight: "bold",
            zIndex: 2,
          }}
        >
          {
            card.foil === 1 ? "Gold Foil" :
            card.foil === 2 ? "Gold Arcana" :
            card.foil === 3 ? "Black Foil" :
            card.foil === 4 ? "Black Arcana" :
            "Regular"
          }
        </Box>
      )}

      {/* Kartenbild */}
      <Box 
        sx={{ 
          pt: { xs: 0.5, sm: 2 }, 
          px: { xs: 0.5, sm: 2 }, 
          position: "relative",
          flex: "1 0 auto",
          display: "flex",
          justifyContent: "center"
        }}
      >
        <Box 
          sx={{ 
            height: { xs: 140, sm: 280 }, 
            width: "100%",
            position: "relative",
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            border: `2px solid ${borderColor}`,
            transition: "transform 0.3s",
            transform: isHovered ? "scale(1.05)" : "scale(1)"
          }}
        >
          <LazyCardImage 
            card={card} 
            details={details} 
            showBaseImages={showBaseImages} 
            alt={details.name}
            key={refreshImageKey} 
            showArcanaEffect={details?.type === "Arcana"} 
          />
        </Box>
      </Box>
      
      {/* Karteninfos */}
      <Box sx={{ flexGrow: 1, pt: { xs: 0.25, sm: 1 }, px: { xs: 0.5, sm: 2 }, pb: { xs: 0.25, sm: 2 } }}>
        <Typography 
          variant="body1" 
          fontWeight="bold" 
          title={details.name}
          sx={{ 
            textAlign: "center", 
            mb: { xs: 0.25, sm: 1 },
            fontSize: { xs: '0.65rem', sm: '1rem' },
            lineHeight: { xs: 1.2, sm: 1.4 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: { xs: '1.5rem', sm: 'auto' }
          }}
        >
          {details.name}
        </Typography>
        

        
        {/* Preis - nur USD-Anzeige (da API bereits USD liefert) - nur für Market-Karten */}
        {(totalUsdPrice > 0 || usdPriceBcx > 0) && (
          <Box 
            mt={{ xs: 0.5, sm: 2 }} 
            display="flex" 
            flexDirection="row"
            alignItems="baseline"
            justifyContent="center"
            gap={{ xs: 0.75, sm: 1.5 }}
          >
            {/* USD Gesamtpreis */}
            {totalUsdPrice > 0 && (
              <Tooltip title="USD total price">
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.greenAccent[400],
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  }}
                >
                  ${totalUsdPrice < 1 ? totalUsdPrice.toFixed(3) : totalUsdPrice.toFixed(2)}
                </Typography>
              </Tooltip>
            )}

            {/* USD Preis pro BCX */}
            {usdPriceBcx > 0 && (
              <Tooltip title="USD price per BCX">
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.blueAccent[400],
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  ${usdPriceBcx < 0.01 ? usdPriceBcx.toFixed(4) : usdPriceBcx.toFixed(3)} / BCX
                </Typography>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Collection Info - für Sammlung-Karten (wenn keine Preise vorhanden) */}
        {!(totalUsdPrice > 0 || usdPriceBcx > 0) && (
          <Box 
            mt={2} 
            display="flex" 
            flexDirection="column"
            alignItems="center"
            gap={0.8}
          >
            {/* Collection Count - show how many of this card type the user owns */}
            {card.collectionCount && card.collectionCount > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  backgroundColor: colors.greenAccent[700],
                  borderRadius: '8px',
                  padding: '4px 8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  width: '100%',
                }}
              >
                <Tooltip title={`You own ${card.collectionCount} of this card`}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: '#FFFFFF',
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.3,
                    }}
                  >
                    📦 {card.collectionCount}x
                  </Typography>
                </Tooltip>
              </Box>
            )}
            
            {/* Level Info */}
            {card.level && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  borderRadius: '8px',
                  padding: '4px 8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  width: '100%',
                }}
              >
                <Tooltip title="Card Level">
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: '#FFFFFF',
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.3,
                    }}
                  >
                    Level {card.level}
                  </Typography>
                </Tooltip>
              </Box>
            )}

            {/* Edition Info */}
            {editionInfo && editionInfo.name !== 'Unknown' && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  backgroundColor: colors.yellow[700],
                  borderRadius: '8px',
                  padding: '2px 6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  width: '100%',
                }}
              >
                <Tooltip title={`Edition: ${editionInfo.name}`}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{
                      color: '#000000',
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.3,
                      fontSize: '0.7rem'
                    }}
                  >
                    {editionInfo.name}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default CardItem;
